import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { clinicInfo } from '@/data/clinicInfo';

export interface SiteContacts {
  telephone: string;
  telephoneDisplay: string;
  email: string;
  whatsapp: string;
  addressHe: string;
  openingHoursDisplay: string;
  instagram: string;
  facebook: string;
}

const DEFAULTS: SiteContacts = {
  telephone: clinicInfo.telephone,
  telephoneDisplay: clinicInfo.telephoneDisplay,
  email: clinicInfo.email,
  whatsapp: clinicInfo.whatsapp,
  addressHe: clinicInfo.address.streetHe,
  openingHoursDisplay: clinicInfo.openingHoursDisplay,
  instagram: clinicInfo.social.instagram,
  facebook: clinicInfo.social.facebook,
};

// Module-level cache so navigating between pages doesn't re-fetch / flicker.
let cached: SiteContacts | null = null;

function mapRow(row: Record<string, unknown> | null): SiteContacts {
  if (!row) return DEFAULTS;
  return {
    telephone: (row.telephone as string) || DEFAULTS.telephone,
    telephoneDisplay: (row.telephone_display as string) || DEFAULTS.telephoneDisplay,
    email: (row.email as string) || DEFAULTS.email,
    whatsapp: (row.whatsapp as string) || DEFAULTS.whatsapp,
    addressHe: (row.address_he as string) || DEFAULTS.addressHe,
    openingHoursDisplay: (row.opening_hours_display as string) || DEFAULTS.openingHoursDisplay,
    instagram: (row.instagram as string) || DEFAULTS.instagram,
    facebook: (row.facebook as string) || DEFAULTS.facebook,
  };
}

export function useSiteContacts(): SiteContacts {
  const [data, setData] = useState<SiteContacts>(cached ?? DEFAULTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: row } = await supabase
        .from('site_contacts')
        .select('*')
        .eq('id', 'default')
        .maybeSingle();
      if (cancelled) return;
      const next = mapRow(row);
      cached = next;
      setData(next);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}
